import { useCallback, useEffect, useState } from 'react';
import { AlertIcon, Badge, Empty, Field, Note, Panel, Spinner } from '@wyocspay/ui';
import { ApiError, api } from './api.js';
import { badgeLabel, dateTime, relative } from './format.js';

/**
 * Заявките на търговците за промяна на данни по профила им.
 *
 * Стои до "Заявки за функции", а не вътре в него, и това е нарочно. Двете
 * опашки си приличат външно, но питат различни неща: там се преценява дали
 * дадена възможност да бъде отключена, с разговор, въпроси и условия по
 * решението; тук се преценява една-единствена стойност - "това ли трябва да
 * пише в полето" - и цялата тежест пада върху нещо, което другият поток няма:
 * дали полето не се е променило, докато заявката е чакала. Сливането им би
 * означавало един компонент с две несвързани половини, всяка от които скрита
 * зад проверка коя точно заявка се гледа.
 */

function useLoad<T>(load: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async (silent?: unknown) => {
    const quiet = silent === true;
    if (!quiet) setLoading(true);
    try {
      setData(await load());
      setError(null);
    } catch (err) {
      if (!quiet) setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      if (!quiet) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void run();
  }, [run]);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void run(true);
    }, 5000);
    return () => clearInterval(id);
  }, [run]);

  return { data, error, loading, reload: run };
}

export function DetailRequestsView() {
  const [status, setStatus] = useState('SUBMITTED');
  const { data, error, loading, reload } = useLoad(() => api.detailRequests(status), [status]);
  const [note, setNote] = useState<Record<string, string>>({});
  const [acknowledged, setAcknowledged] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);

  const filters = ['SUBMITTED', 'APPROVED', 'DENIED', 'WITHDRAWN', 'all'];

  async function decide(
    row: { id: string; stale: boolean; live_value: string | null },
    approve: boolean,
  ): Promise<void> {
    setBusy(row.id);
    setMsg(null);
    try {
      await api.decideDetailRequest(row.id, {
        approve,
        note: note[row.id] ?? '',
        /**
         * Изпраща се само при одобрение на остаряла заявка, и е точно
         * стойността, която този екран показа като текуща. Сървърът я сравнява
         * с действителната и отказва, ако не съвпадат - така "одобри въпреки
         * това" не може да се случи, без някой да е видял какво заменя.
         */
        acknowledged_current_value:
          approve && row.stale ? (row.live_value ?? '') : undefined,
      });
      setMsg({
        tone: 'ok',
        text: approve ? 'Одобрено и приложено по профила.' : 'Отказано. Търговецът вижда причината.',
      });
      setNote((s) => ({ ...s, [row.id]: '' }));
      setAcknowledged((s) => ({ ...s, [row.id]: false }));
      await reload();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Note title="Тук се променят само наши данни">
        Търговецът иска, вие решавате, и одобрението записва стойността в нашата база. Данните, които
        Stripe държи и проверява - юридическото лице, представителят, банковата сметка, документите -
        никога не минават оттук: за тях търговецът получава в панела си линк към съответния вграден
        компонент на Stripe, а платформата не ги приема и не ги съхранява.
      </Note>

      <Note tone="warn" title="Цените и статусът не са в тази опашка">
        Промяната на такса или на статуса на профил е подписана команда от регистрирано устройство,
        която при <em>set_fee</em>, <em>reinstate</em> и <em>close</em> изчаква и втори администратор.
        Бутон "Одобри" тук не може да произведе втори подпис, затова тези две неща остават на
        страницата на търговеца и в опашката за второ одобрение.
      </Note>

      {msg ? <Note tone={msg.tone}>{msg.text}</Note> : null}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {filters.map((f) => (
          <button
            key={f}
            type="button"
            className={`wc-btn wc-btn-sm ${status === f ? '' : 'wc-btn-secondary'}`}
            onClick={() => setStatus(f)}
          >
            {f === 'all' ? 'Всички' : badgeLabel(f)}
          </button>
        ))}
      </div>

      {loading ? <Spinner label="Зареждане…" /> : error ? <Note tone="danger">{error}</Note> : null}

      {data && data.data.length === 0 ? (
        <Panel>
          <Empty>Няма нищо тук.</Empty>
        </Panel>
      ) : null}

      {data?.data.map((r) => {
        const open = r.status === 'SUBMITTED';
        const blocked = open && r.stale && !acknowledged[r.id];

        return (
          <Panel key={r.id}>
            <div
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'center',
                flexWrap: 'wrap',
                marginBottom: 10,
              }}
            >
              <strong style={{ fontSize: 15 }}>{r.field_label}</strong>
              <Badge
                tone={
                  r.status === 'APPROVED' ? 'ok'
                  : r.status === 'DENIED' ? 'fail'
                  : r.status === 'WITHDRAWN' ? 'neutral'
                  : 'pending'
                }
              >
                {badgeLabel(r.status)}
              </Badge>
              <span style={{ fontSize: 13 }}>{r.merchant.name}</span>
              <span className="wc-num" style={{ fontSize: 12, color: 'var(--wc-faint)' }}>
                {r.merchant.id}
              </span>
              <span style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>{relative(r.created_at)}</span>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 12,
                marginBottom: 12,
              }}
            >
              <Value label="При подаване е било" value={r.current_value} />
              <Value label="Иска да стане" value={r.requested_value} accent="var(--wc-blue-ink)" />
              {open && r.stale ? (
                <Value label="В момента е" value={r.live_value ?? ''} accent="var(--wc-orange)" />
              ) : null}
              {!open && r.applied_value !== null && r.applied_value !== r.current_value ? (
                <Value label="Заменена стойност" value={r.applied_value} accent="var(--wc-orange)" />
              ) : null}
            </div>

            {r.reason ? (
              <p style={{ margin: '0 0 12px', fontSize: 13.5, color: 'var(--wc-body)' }}>
                <span className="wc-label" style={{ display: 'block', marginBottom: 2 }}>
                  Търговецът пише
                </span>
                {r.reason}
              </p>
            ) : null}

            {r.note ? (
              <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--wc-muted)' }}>{r.note}</p>
            ) : null}

            {open && r.stale ? (
              <div style={{ marginBottom: 12 }}>
                <Note tone="warn" title="Полето е променено, откакто търговецът е попитал">
                  <p style={{ margin: '0 0 8px' }}>
                    <AlertIcon size={12} /> Заявката е подадена срещу{' '}
                    <strong>&bdquo;{r.current_value}&ldquo;</strong>, а сега там пише{' '}
                    <strong>&bdquo;{r.live_value}&ldquo;</strong>. Одобрението ще замени по-новата
                    стойност, а не онази, която търговецът е виждал.
                  </p>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13.5 }}>
                    <input
                      type="checkbox"
                      checked={acknowledged[r.id] ?? false}
                      onChange={(e) => setAcknowledged((s) => ({ ...s, [r.id]: e.target.checked }))}
                      style={{ marginTop: 3, width: 16, height: 16, flex: 'none' }}
                    />
                    <span>
                      Видях по-новата стойност и въпреки това искам да бъде заменена с исканата.
                    </span>
                  </label>
                </Note>
              </div>
            ) : null}

            {open ? (
              <>
                <Field
                  label="Бележка към решението"
                  hint="Задължителна при отказ - търговецът я чете и от нея разбира какво да направи."
                >
                  <input
                    className="wc-input"
                    value={note[r.id] ?? ''}
                    onChange={(e) => setNote((s) => ({ ...s, [r.id]: e.target.value }))}
                    placeholder="Защо одобрявате или отказвате"
                  />
                </Field>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    type="button"
                    className="wc-btn wc-btn-sm"
                    disabled={busy === r.id || blocked}
                    onClick={() => void decide(r, true)}
                  >
                    {busy === r.id ? 'Изпълнява се…' : 'Одобри и приложи'}
                  </button>
                  <button
                    type="button"
                    className="wc-btn wc-btn-sm wc-btn-danger"
                    disabled={busy === r.id || (note[r.id] ?? '').trim().length < 3}
                    onClick={() => void decide(r, false)}
                  >
                    Откажи
                  </button>
                  {blocked ? (
                    <span style={{ fontSize: 12.5, color: 'var(--wc-orange)' }}>
                      Потвърдете по-горе, преди да одобрите.
                    </span>
                  ) : (note[r.id] ?? '').trim().length < 3 ? (
                    <span style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>
                      Отказът иска причина.
                    </span>
                  ) : null}
                </div>
              </>
            ) : (
              <p style={{ margin: 0, fontSize: 13, color: 'var(--wc-muted)' }}>
                {r.status === 'WITHDRAWN' ? 'Оттеглена от търговеца' : 'Решено'} от{' '}
                <span className="wc-num">{r.decided_by ?? '-'}</span> {relative(r.decided_at)}
                {r.decision_note ? (
                  <>
                    {' '}
                    · <span style={{ color: 'var(--wc-body)' }}>{r.decision_note}</span>
                  </>
                ) : null}
                {r.decided_at ? (
                  <span style={{ color: 'var(--wc-faint)' }}> · {dateTime(r.decided_at)}</span>
                ) : null}
              </p>
            )}
          </Panel>
        );
      })}
    </div>
  );
}

function Value({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div>
      <span className="wc-label" style={{ display: 'block', marginBottom: 2 }}>
        {label}
      </span>
      <span
        style={{
          fontSize: 14,
          color: accent ?? 'var(--wc-body)',
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
        }}
      >
        {value.length > 0 ? value : <span style={{ color: 'var(--wc-faint)' }}>празно</span>}
      </span>
    </div>
  );
}
